import type { ReactNode } from "react";
import { Modal } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../hooks/useViewport";
import { R } from "../orderingTheme";
import {
  posModalStyles as modalStyles,
  ModalHeader,
  ModalFooter,
  GhostButton,
  PrimaryButton,
} from "../modalPrimitives";

interface ConfirmActionModalProps {
  open: boolean;
  /** Какво е положението — едно изречение, меко (сиво). */
  message: string;
  /** Какво ще стане, ако продължи — по-тъмно и по-тежко от `message`. */
  question?: string;
  confirmLabel?: string;
  title: string;
  icon?: ReactNode;
  /** Етикет на затварящия бутон; по подразбиране „Отказ". */
  cancelLabel?: string;
  onCancel: () => void;
  /** Без `onConfirm` модалът е само информативен — единствен бутон затваря. */
  onConfirm?: () => void;
}

/**
 * Потвърждение „положението е такова — продължавам ли?" за действията по маси
 * (разделяне в чужда маса, прехвърляне и възстановяване, които биха дублирали
 * име). Не е забрана: BE връща 409, тук операторът решава и заявката се повтаря
 * с изричното потвърждение. Без `onConfirm` служи като информативно съобщение
 * (напр. заета маса на друг сервитьор без право за прехвърляне).
 */
export const ConfirmActionModal = ({
  open,
  message,
  question,
  confirmLabel,
  title,
  icon,
  cancelLabel,
  onCancel,
  onConfirm,
}: ConfirmActionModalProps) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet } = useViewport();

  const width = isMobile ? undefined : isTablet ? 420 : 460;
  const bodyPad = isNarrow ? "16px" : "20px 24px";

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div style={{ background: R.surface, fontFamily: R.font, color: R.text }}>
        <ModalHeader
          icon={icon ?? <ExclamationCircleOutlined />}
          title={title}
          onClose={onCancel}
          isNarrow={isNarrow}
        />

        <div
          style={{
            padding: bodyPad,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: 13.5,
              fontWeight: 500,
              color: R.textSoft,
              lineHeight: 1.55,
            }}
          >
            {message}
          </p>
          {question && (
            <p
              style={{
                margin: 0,
                fontSize: 13.5,
                fontWeight: 600,
                color: R.ink,
                lineHeight: 1.55,
              }}
            >
              {question}
            </p>
          )}
        </div>

        <ModalFooter isNarrow={isNarrow}>
          {/* Масив, не фрагмент: ModalFooter брои децата си през
              Children.toArray, а фрагментът е ЕДНО дете — двата бутона
              падаха в една клетка на решетката, без отстъп помежду им. */}
          {onConfirm && confirmLabel ? (
            [
              <GhostButton
                key="cancel"
                label={cancelLabel ?? t("common.cancel")}
                onClick={onCancel}
              />,
              <PrimaryButton
                key="confirm"
                label={confirmLabel}
                onClick={onConfirm}
              />,
            ]
          ) : (
            <PrimaryButton
              label={cancelLabel ?? t("common.cancel")}
              onClick={onCancel}
            />
          )}
        </ModalFooter>
      </div>
    </Modal>
  );
};
